import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { ShieldCheck, Sparkles } from 'lucide-react';
import { AppDispatch } from '../../redux/store';
import { logout } from '../../redux/auth/authSlice';
import Sidebar from '../../components/sidebar/Sidebar';
import {
  ResponsiveAppShell,
  TaskyButton,
  TaskyPageHeader,
  TaskySegmentedControl,
} from '../../components/tasky';
import { TaskyHeaderActions } from '../../components/tasky/layout/TaskyHeaderActions';
import TaapProposalCard from '../../components/taap/TaapProposalCard';
import TaapProductEmptyState from '../../components/taap/TaapProductEmptyState';
import TaapScopePicker from '../../components/taap/TaapScopePicker';
import TaapTabs, { adOperationsPath } from '../../components/taap/TaapTabs';
import { useTaapProductScope } from '../../components/taap/useTaapProductScope';
import taapService, { toScopeParams } from '../../services/api/taapService';
import { TaapProposal } from '../../types/taap';

type Tab = 'pending' | 'executed' | 'rejected';

const TABS: { key: Tab; label: string }[] = [
  { key: 'pending', label: '未承認' },
  { key: 'executed', label: '承認・実行済み' },
  { key: 'rejected', label: '却下' },
];

const TAB_SEGMENTS = TABS.map((option) => ({ value: option.key, label: option.label }));

/**
 * 提案・承認。
 *
 * 提案は自動で生成されるが、配信が変わるのは承認したときだけ。その前提を
 * 画面の一番上で明示している（利用者が「勝手に変わるのでは」と不安に
 * ならないことが、この機能を使ってもらう前提になる）。
 */
const TaapProposalsPage: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const { search } = useLocation();
  const scope = useTaapProductScope();
  const { productId, accountId, recoverToFirstProduct, selectProduct, selectAccount } = scope;
  const ready = productId !== null;
  const loadId = useRef(0);

  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [tab, setTab] = useState<Tab>('pending');
  const scopeKey = `${productId}:${accountId}:${tab}`;
  const currentScope = useRef(scopeKey);
  currentScope.current = scopeKey;
  const [proposals, setProposals] = useState<TaapProposal[]>([]);
  const [pendingCount, setPendingCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [analyzing, setAnalyzing] = useState(false);
  const [message, setMessage] = useState<string | null>(null);

  const load = useCallback(async () => {
    if (!ready || currentScope.current !== scopeKey) return;
    const requestId = ++loadId.current;
    setLoading(true);
    setProposals([]);
    try {
      const data = await taapService.getProposals(tab, toScopeParams(productId, accountId));
      if (requestId !== loadId.current) return;
      setProposals(data.proposals);
      setPendingCount(data.counts.pending);
    } catch (e) {
      if (requestId !== loadId.current) return;
      console.error('TAAP proposals load failed:', e);
      setPendingCount(0);
      // 紐付けが外れた商品・その商品に無いアカウントの id が URL に残っているケース。
      if ((e as { response?: { status?: number } })?.response?.status === 404 && (await recoverToFirstProduct({ productId, accountId }))) {
        if (requestId === loadId.current) setMessage('商品・アカウントが見つからないため、先頭の商品に戻しました。');
        return;
      }
      if (requestId === loadId.current) setMessage('提案の読み込みに失敗しました。');
    } finally {
      if (requestId === loadId.current) setLoading(false);
    }
  }, [ready, tab, productId, accountId, scopeKey, recoverToFirstProduct]);

  useEffect(() => {
    load();
    return () => { loadId.current += 1; };
  }, [load]);

  const handleApprove = async (proposal: TaapProposal) => {
    setBusyId(proposal.id);
    setMessage(null);
    try {
      await taapService.approveProposal(proposal.id);
      if (currentScope.current !== scopeKey) return;
      setMessage('承認しました。');
      await load();
    } catch (e) {
      console.error('TAAP approve failed:', e);
      if (currentScope.current !== scopeKey) return;
      setMessage('承認に失敗しました。');
    } finally {
      setBusyId(null);
    }
  };

  const handleReject = async (proposal: TaapProposal) => {
    // 却下理由は次の診断で同じ提案を出さないための学習材料になるので、
    // 任意入力にしつつ必ず尋ねる。
    const reason = window.prompt('却下の理由（任意）') ?? undefined;

    setBusyId(proposal.id);
    setMessage(null);
    try {
      await taapService.rejectProposal(proposal.id, reason);
      if (currentScope.current !== scopeKey) return;
      setMessage('却下しました。同じ所見は30日間再提案されません。');
      await load();
    } catch (e) {
      console.error('TAAP reject failed:', e);
      if (currentScope.current !== scopeKey) return;
      setMessage('却下に失敗しました。');
    } finally {
      setBusyId(null);
    }
  };

  const handleAnalyzeNow = async () => {
    setAnalyzing(true);
    setMessage(null);
    try {
      const result = await taapService.generateProposals(toScopeParams(productId, accountId));
      if (currentScope.current !== scopeKey) return;
      setMessage(
        result.created_count > 0
          ? `分析が完了し、新しい提案が${result.created_count}件見つかりました。`
          : '新しい提案はありませんでした。',
      );
      await load();
    } catch (e) {
      console.error('TAAP generate failed:', e);
      if (currentScope.current !== scopeKey) return;
      setMessage('分析の実行に失敗しました。');
    } finally {
      setAnalyzing(false);
    }
  };

  const handleLogout = () => {
    dispatch(logout());
    navigate('/login');
  };

  const sidebar = (
    <Sidebar onLogout={handleLogout} onToggleCollapse={setSidebarCollapsed} collapsed={sidebarCollapsed} />
  );

  return (
    <ResponsiveAppShell
      sidebar={sidebar}
      mobileSidebar={<Sidebar onLogout={handleLogout} />}
      topbarTitle="提案・承認"
    >
      <div className="flex min-h-[calc(100dvh_-_56px)] bg-warm-bg lg:h-screen lg:min-h-0">
        <div className="flex flex-1 flex-col overflow-y-auto overflow-x-hidden">
          <TaskyPageHeader
            data-testid="ad-operations-header"
            title="広告運用ダッシュボード"
            description={
              <>
                <Link to="/account/settings#notification-integrations" className="text-primary underline">
                  レポート通知設定
                </Link>
              </>
            }
            actions={<span className="hidden items-center lg:flex"><TaskyHeaderActions /></span>}
            toolbar={
              ready ? (
                <TaapScopePicker
                  scope={scope}
                  onSelectProduct={(id) => { setMessage(null); selectProduct(id); }}
                  onSelectAccount={(id) => { setMessage(null); selectAccount(id); }}
                />
              ) : undefined
            }
          />

          <div className="mx-auto w-full max-w-[1400px] p-6 max-[640px]:p-4">
            <div className="mb-4" data-tutorial="taap-proposals-tabs">
              <TaapTabs active="proposals" pendingCount={pendingCount} />
            </div>

            {ready ? (
              <>
                <div className="mt-4 flex items-start gap-2 rounded-xl border border-emerald-200 bg-emerald-50 px-5 py-3 text-sm text-emerald-900">
                  <ShieldCheck className="mt-0.5 h-4 w-4 shrink-0" />
                  <div>
                    <strong className="font-semibold">承認は方針の記録です。バナー作成・広告の変更は別途行います。</strong>
                  </div>
                </div>

                <div className="mt-4 flex flex-wrap items-center justify-between gap-2" data-tutorial="taap-proposals-filter">
                  <TaskySegmentedControl<Tab>
                    aria-label="提案の状態で絞り込む"
                    options={TAB_SEGMENTS.map((option) => (
                      option.value === 'pending' ? { ...option, count: pendingCount } : option
                    ))}
                    value={tab}
                    onChange={setTab}
                  />

                  <TaskyButton
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={handleAnalyzeNow}
                    disabled={analyzing}
                    className="border border-warm-border bg-white text-primary hover:bg-warm-bg"
                  >
                    <Sparkles className={analyzing ? 'animate-pulse' : undefined} />
                    {analyzing ? '分析中…' : '今すぐ分析を実行'}
                  </TaskyButton>
                </div>

                {message && (
                  <div className="mt-3 rounded-lg border border-gray-200 bg-white px-4 py-2 text-sm text-gray-700">
                    {message}
                  </div>
                )}

                <div className="mt-4 space-y-3" data-tutorial="taap-proposals-list">
                  {loading ? (
                    <div className="rounded-xl border border-gray-200 bg-white px-5 py-8 text-center text-sm text-gray-500">
                      読み込み中…
                    </div>
                  ) : proposals.length === 0 ? (
                    <div className="rounded-xl border border-gray-200 bg-white px-5 py-8 text-center text-sm text-gray-500">
                      {tab === 'pending'
                        ? '未承認の提案はありません。'
                        : tab === 'executed'
                          ? '実行済みの提案はまだありません。'
                          : '却下した提案はありません。'}
                    </div>
                  ) : (
                    proposals.map((proposal) => (
                      <TaapProposalCard
                        key={proposal.id}
                        proposal={proposal}
                        busy={busyId === proposal.id}
                        onApprove={handleApprove}
                        onReject={handleReject}
                        onOpenDetail={() => navigate(adOperationsPath(`/taap/proposals/${proposal.id}`, search))}
                      />
                    ))
                  )}
                </div>
              </>
            ) : (
              <div className="mt-4">
                {message && (
                  <div className="mb-3 rounded-lg border border-gray-200 bg-white px-4 py-2 text-sm text-gray-700">{message}</div>
                )}
                {scope.loadFailed ? (
                  <div className="rounded-xl border border-red-200 bg-red-50 px-5 py-3 text-sm text-red-800">
                    商品を読み込めませんでした。時間をおいて再度お試しください。
                  </div>
                ) : scope.loaded ? (
                  <TaapProductEmptyState />
                ) : null}
              </div>
            )}
          </div>
        </div>
      </div>
    </ResponsiveAppShell>
  );
};

export default TaapProposalsPage;
